import React, { useState, useEffect } from "react";
import { useSettings } from "../../context/SettingsContext";
import aflPlayerData from "../../assets/afl_player_data_2026.json";

// Marks the leading player for a dataset. Same shape/gold as the BYO comparison chart.
const Crown = ({ className = 'w-3.5 h-2.5' }) => (
  <svg className={className} viewBox="0 0 14 10" fill="none" aria-hidden="true">
    <path d="M0 10 L0 1.5 L3.5 5 L7 0 L10.5 5 L14 1.5 L14 10 Z" fill="#fbbf24" opacity="0.95" />
  </svg>
);

const ComparisonVoteBarChart = ({ selectedPlayers = [], voteData, onThresholdChange, showThreshold = false }) => {
  const { getEnabledKeys } = useSettings();
  const enabledSourceKeys = getEnabledKeys();
  const enabledCount = enabledSourceKeys.length || 1;

  // Display order for charts (C1..C5 then external)
  const displayOrder = ['count1','count2','count3','count4','count5','betfair','espn','byb'];
  const enabledDisplayKeys = displayOrder.filter((k) => new Set(enabledSourceKeys).has(k));
  const keyToLabel = { 
    count1: 'BG 1', count2: 'BG 2', count3: 'BG 3', count4: 'BG 4', count5: 'BG 5', 
    betfair: 'Betfair', espn: 'ESPN', byb: 'BYB' 
  };

  // Get player data for selected players
  const playersData = React.useMemo(() => {
    if (!voteData?.leaderboard || selectedPlayers.length === 0) return [];
    
    return selectedPlayers.map(playerName => {
      const playerData = voteData.leaderboard.find(p => p.name === playerName);
      if (!playerData) return null;
      
      return playerData;
    }).filter(Boolean);
  }, [selectedPlayers, voteData?.leaderboard]);

  // Get all count values for range calculation
  const allValues = React.useMemo(() => {
    const values = [];
    for (const player of playersData) {
      for (const key of enabledDisplayKeys) {
        const value = Number(player[key] ?? 0);
        if (!isNaN(value)) values.push(value);
      }
    }
    return values;
  }, [playersData, enabledDisplayKeys]);

  const maxCount = allValues.length > 0 ? Math.max(...allValues) : 30;
  const minCount = allValues.length > 0 ? Math.min(...allValues) : 0;
  
  // Calculate average for default threshold
  const avgCount = allValues.length > 0 ? allValues.reduce((sum, count) => sum + count, 0) / allValues.length : 15;
  
  // Set default threshold to nearest .5 (always ending in .5)
  const defaultThreshold = Math.floor(avgCount) + 0.5;
  
  // Set min and max for slider to nearest .5
  const sliderMin = Math.max(0.5, Math.floor(minCount - 0.5) + 0.5);
  const sliderMax = Math.floor(maxCount) + 0.5;
  
  const [sliderValue, setSliderValue] = useState(defaultThreshold);
  
  // Notify parent component when threshold changes
  useEffect(() => {
    if (onThresholdChange) {
      onThresholdChange(sliderValue);
    }
  }, [sliderValue, onThresholdChange]);
  
  // Calculate range for better visualization with some padding
  const padding = (maxCount - minCount) * 0.1 || 1;
  const visualMin = Math.floor(minCount - padding);
  const visualMax = Math.ceil(maxCount + padding);
  
  // Generate y-axis labels
  const generateYAxisLabels = () => {
    const labels = [];
    const range = visualMax - visualMin;
    const step = range > 10 ? Math.ceil(range / 5) : 1;
    
    for (let i = visualMin; i <= visualMax; i += step) {
      if (i >= 0) labels.push(i);
    }
    return labels;
  };
  
  const yAxisLabels = generateYAxisLabels();
  
  // Function to get display label (shorten Betfair to BF on mobile)
  const getDisplayLabel = (label) => {
    if (label === "Betfair") {
      return (
        <>
          <span className="hidden sm:inline">Betfair</span>
          <span className="inline sm:hidden">BF</span>
        </>
      );
    }
    return label;
  };

  // Function to get player image
  const getPlayerImage = (playerName) => {
    const player = aflPlayerData.find(p => p.player_name === playerName);
    return player ? player.image_src : null;
  };

  // Player colors for consistency (brand-family hues, one per player)
  const playerColors = [
    'bg-emerald-500', 'bg-sky-500', 'bg-amber-500', 'bg-violet-500', 
    'bg-pink-500', 'bg-teal-500', 'bg-indigo-500', 'bg-orange-500'
  ];

  // Calculate bar heights directly without animation to avoid infinite loops
  const barHeights = React.useMemo(() => {
    if (playersData.length === 0 || enabledDisplayKeys.length === 0) {
      return {};
    }
    
    // Calculate visualMin and visualMax
    const values = [];
    for (const player of playersData) {
      for (const key of enabledDisplayKeys) {
        const value = Number(player[key] ?? 0);
        if (!isNaN(value)) values.push(value);
      }
    }
    
    const effectMaxCount = values.length > 0 ? Math.max(...values) : 30;
    const effectMinCount = values.length > 0 ? Math.min(...values) : 0;
    const effectPadding = (effectMaxCount - effectMinCount) * 0.1 || 1;
    const effectVisualMin = Math.floor(effectMinCount - effectPadding);
    const effectVisualMax = Math.ceil(effectMaxCount + effectPadding);
    
    const heights = {};
    playersData.forEach((player, playerIndex) => {
      enabledDisplayKeys.forEach((key, countIndex) => {
        const count = Number(player[key] ?? 0);
        const normalizedHeight = ((count - effectVisualMin) / (effectVisualMax - effectVisualMin)) * 100;
        heights[`${playerIndex}-${countIndex}`] = Math.max(normalizedHeight, 2);
      });
    });
    
    return heights;
  }, [playersData, enabledDisplayKeys]);

  // Show empty chart structure even when no players are selected
  const showEmptyChart = selectedPlayers.length === 0;

  // Every bar needs room for the player photo sitting underneath it, so the plot
  // claims a minimum width and scrolls sideways once the comparison outgrows the
  // panel instead of squeezing the bars together. Photos are hidden on mobile,
  // so the floor there only has to keep the bars readable.
  const plotMinWidth = (perBar) => {
    const players = playersData.length;
    const groups = enabledDisplayKeys.length;
    if (!players || !groups) return 0;
    const groupWidth = players * perBar + (players - 1) * 4; // gap-x-1 between bars
    return groups * groupWidth + (groups - 1) * 8 + 16; // gap-x-2 between groups + px-2
  };

  return (
    <div className="bg-slate-950/60 rounded-xl border border-slate-800 p-3 w-full h-full flex flex-col min-h-0">
      <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-slate-500 mb-2 flex-shrink-0">
        Vote Visualization
      </div>
      {/* Chart Container */}
      <div className="relative h-64 md:h-80 pb-16 w-full">
        <div className="flex h-full gap-0 w-full">
          {/* Y-axis labels */}
          <div className="relative pr-2">
            {yAxisLabels.map((label) => {
              const position = ((visualMax - label) / (visualMax - visualMin)) * 100;
              return (
                <div 
                  key={label} 
                  className="absolute right-0 text-[10px] font-mono tabular-nums text-slate-500"
                  style={{ 
                    top: `${position}%`,
                    transform: 'translateY(-50%)'
                  }}
                >
                  {label}
                </div>
              );
            })}
          </div>
          
          {/* Chart area - the scroller extends past the plot so the player
              photos below the axis are never clipped */}
          <div className="flex-1 min-w-0 relative">
          <div className="absolute inset-0 -bottom-16 overflow-x-auto overflow-y-hidden styled-scrollbar">
          <div
            className="h-[calc(100%-4rem)] bg-[#0a1120] border border-slate-800/70 rounded-lg relative overflow-visible pb-8 min-w-[var(--bars-min-w)] md:min-w-[var(--bars-min-w-md)]"
            style={{
              '--bars-min-w': `${plotMinWidth(18)}px`,
              '--bars-min-w-md': `${plotMinWidth(30)}px`,
            }}
          >
            {/* Grid lines */}
            {yAxisLabels.map((label) => {
              const position = ((visualMax - label) / (visualMax - visualMin)) * 100;
              return (
                <div 
                  key={`grid-${label}`}
                  className="absolute left-0 right-0 border-t border-slate-800"
                  style={{ top: `${position}%` }}
                />
              );
            })}
            
            {/* Horizontal threshold line - only show if showThreshold is true */}
            {showThreshold && (
              <div 
                className="absolute left-0 right-0 border-t border-dashed border-slate-200/70 z-10"
                style={{ 
                  top: `${((visualMax - sliderValue) / (visualMax - visualMin)) * 100}%`
                }}
              />
            )}
            
            {/* Vertical separators between count groups */}
            {enabledDisplayKeys.length > 1 && enabledDisplayKeys.slice(0, -1).map((_, index) => (
              <div 
                key={`separator-${index}`}
                className="absolute top-0 bottom-0 border-l border-dashed border-slate-700/60 z-5"
                style={{ 
                  left: `${((index + 1) / enabledDisplayKeys.length) * 100}%`
                }}
              />
            ))}
            
            {/* Count labels at top */}
            <div className="absolute top-0 left-0 right-0 flex justify-center gap-x-2 px-2 pt-1">
              {enabledDisplayKeys.map((key) => (
                <div key={`label-${key}`} className="flex-1 flex justify-center">
                  <span className="text-slate-500 text-[10px] font-mono uppercase tracking-[0.1em]">
                    {getDisplayLabel(keyToLabel[key])}
                  </span>
                </div>
              ))}
            </div>

            {/* Empty state message */}
            {showEmptyChart && (
              <div className="absolute inset-0 flex items-center justify-center">
                <p className="text-slate-500 text-xs font-mono">Select players to see comparison data</p>
              </div>
            )}

            {/* Bars container - grouped by count */}
            {!showEmptyChart && (
              <div className="absolute inset-0 flex items-end justify-center gap-x-2 px-2 pt-6">
                {enabledDisplayKeys.map((key, countIndex) => (
                <div key={key} className="flex-1 flex flex-col items-center justify-end h-full relative min-w-0">
                  {/* Player bars for this count - side by side */}
                  <div className="relative w-full h-full flex items-end justify-center gap-x-1">
                    {playersData.map((player, playerIndex) => {
                      const count = Number(player[key] ?? 0);
                      const isAboveLine = showThreshold ? count > sliderValue : true;
                      const heightKey = `${playerIndex}-${countIndex}`;
                      const playerColor = playerColors[playerIndex % playerColors.length];
                      const normalizedHeight = ((count - visualMin) / (visualMax - visualMin)) * 100;
                      const barHeight = Math.max(normalizedHeight, 2);
                      
                      // Determine if this player has the highest count for this key
                      const maxCountForKey = Math.max(...playersData.map(p => Number(p[key] ?? 0)));
                      const isWinner = count === maxCountForKey && count > 0;
                      
                      return (
                        <div key={player.name} className="flex-1 flex flex-col items-center justify-end h-full relative min-w-0">
                          {/* Bar */}
                          <div
                            className={`w-full ${isAboveLine ? playerColor : 'bg-rose-500'} rounded-t transition-all duration-300 min-h-[2px] relative`}
                            style={{ 
                              height: `${barHeights[heightKey] || barHeight}%`,
                              transition: 'background-color 300ms ease'
                            }}
                            title={`${player.name}: ${count.toFixed(2)}`}
                          >
                            {/* Crown for winner - sits above the bar - hidden on mobile */}
                            {isWinner && (
                              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden md:block">
                                <Crown />
                              </div>
                            )}
                          </div>
                          
                          {/* Player Photo - positioned at bottom (X-axis) - hidden on mobile.
                              w-max keeps the photo at its natural size instead of being
                              shrink-to-fit down to half the bar slot. */}
                          <div className="absolute bottom-0 left-1/2 w-max transform -translate-x-1/2 translate-y-full flex-col items-center hidden md:flex">
                            <div className="mb-1">
                              {(() => {
                                const playerImageSrc = getPlayerImage(player.name);
                                return playerImageSrc ? (
                                  <img 
                                    src={playerImageSrc} 
                                    alt={player.name}
                                    className="w-7 h-5 rounded object-cover flex-shrink-0 ring-1 ring-slate-700/60"
                                  />
                                ) : (
                                  <div className="w-7 h-5 bg-slate-800 ring-1 ring-slate-700/60 rounded flex items-center justify-center flex-shrink-0">
                                    <span className="text-white font-bold text-[10px] md:text-xs">{player.image}</span>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              </div>
            )}
          </div>
          </div>
          </div>
        </div>
      </div>

      {/* Player Legend */}
      {!showEmptyChart && (
      <div className="mb-4 mt-2">
        <div className="flex flex-wrap gap-1.5 justify-center">
          {playersData.map((player, index) => {
            const playerImageSrc = getPlayerImage(player.name);
            const playerColor = playerColors[index % playerColors.length];
            
            return (
              <div key={player.name} className="flex items-center gap-1.5 bg-slate-900/60 border border-slate-700/60 rounded-md px-2 py-1">
                {playerImageSrc ? (
                  <img 
                    src={playerImageSrc} 
                    alt={player.name}
                    className="w-4 h-4 rounded-full object-cover flex-shrink-0 ring-1 ring-slate-700/60"
                  />
                ) : (
                  <div className="w-4 h-4 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-[10px]">{player.image}</span>
                  </div>
                )}
                <div className={`w-2 h-2 rounded-sm ${playerColor}`}></div>
                <span className="text-slate-200 text-[11px] font-medium">{player.name}</span>
              </div>
            );
          })}
        </div>
      </div>
      )}
      
      {/* Slider Control - only show if showThreshold is true and has players */}
      {showThreshold && !showEmptyChart && (
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-slate-500">Vote Threshold</span>
            <span className="text-emerald-300 text-sm font-mono font-bold tabular-nums">{sliderValue}</span>
          </div>
          
          <div className="relative">
            <input
              type="range"
              min={sliderMin}
              max={sliderMax}
              step="1"
              value={sliderValue}
              onChange={(e) => {
                const val = parseFloat(e.target.value);
                // Ensure value always ends in .5
                const newValue = Math.floor(val) + 0.5;
                setSliderValue(newValue);
              }}
              className="comparison-threshold-slider w-full h-1.5 bg-slate-800 rounded-full appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
              list="tickmarks"
            />
          </div>
          
          <datalist id="tickmarks">
            {(() => {
              const ticks = [];
              for (let i = sliderMin; i <= sliderMax; i += 1) {
                ticks.push(<option key={i} value={i} />);
              }
              return ticks;
            })()}
          </datalist>
          
          <div className="flex justify-between text-[10px] font-mono tabular-nums text-slate-500">
            <span>{sliderMin}</span>
            <span>{sliderMax}</span>
            <span className="ml-auto">n={enabledCount}</span>
          </div>
        </div>
      )}
      
      <style jsx>{`
        .comparison-threshold-slider::-webkit-slider-thumb {
          appearance: none;
          height: 16px;
          width: 16px;
          border-radius: 50%;
          background: #10b981;
          cursor: pointer;
          border: 2px solid #0f172a;
          box-shadow: 0 0 0 1px rgba(16, 185, 129, 0.4);
        }
        
        .comparison-threshold-slider::-moz-range-thumb {
          height: 16px;
          width: 16px;
          border-radius: 50%;
          background: #10b981;
          cursor: pointer;
          border: 2px solid #0f172a;
          box-shadow: 0 0 0 1px rgba(16, 185, 129, 0.4);
        }
      `}</style>
    </div>
  );
};

export default ComparisonVoteBarChart;

